import type {
  MailFolder,
  MeResponse,
  MessageDetail,
  MessageListPage,
  MailSettings,
  OutgoingMessage,
  SignatureSettings,
  UnreadByAccount,
  UploadedAttachment,
} from "./types.js";

/**
 * Eigen header die een cross-site formulier of <img> niet kan meesturen zonder
 * preflight; de server weigert elke schrijfactie zonder deze header.
 */
const CSRF_HEADER = "x-rw-csrf";

/** Welke mailbox een verzoek bedoelt, als er meerdere aangemeld zijn. */
const ACCOUNT_HEADER = "x-rw-account";

/** Willekeurig id van dit tabblad; zie CLIENT_ID. */
const CLIENT_HEADER = "x-rw-client";

/** Elk verzoek loopt over dezelfde origin, dus paden en geen absolute URL's. */
const BASE = "/api";

// ── Account en tabblad ──────────────────────────────────────────────────────

/**
 * De mailbox waar de app nu naar kijkt. Gaat met elk verzoek mee, tenzij een
 * aanroep zelf een account noemt (opstellen vanuit een andere afzender, de
 * instellingen van een ander account).
 */
let activeAccount: string | null = null;

export function setActiveAccount(id: string | null): void {
  activeAccount = id;
}

/**
 * Willekeurig per tabblad. De server stuurt het mee in live-meldingen over
 * wijzigingen, zodat een tabblad zijn eigen wijziging niet nog eens ververst.
 */
export const CLIENT_ID = (() => {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
})();

/** `undefined` = het actieve account, `null` = geen (verzoeken over de hele browser). */
type AccountChoice = string | null | undefined;

function accountFor(choice: AccountChoice): string | null {
  return choice === undefined ? activeAccount : choice;
}

function baseHeaders(choice: AccountChoice): Record<string, string> {
  const headers: Record<string, string> = { [CLIENT_HEADER]: CLIENT_ID };
  const account = accountFor(choice);
  if (account !== null) headers[ACCOUNT_HEADER] = account;
  return headers;
}

/**
 * Voor URL's die de browser zelf ophaalt (iframe, download, afbeelding): daar
 * kan geen header mee, dus het account gaat in de query.
 */
function withAccount(url: string, choice: AccountChoice): string {
  const account = accountFor(choice);
  if (account === null) return url;
  return `${url}${url.includes("?") ? "&" : "?"}acct=${encodeURIComponent(account)}`;
}

/**
 * Fout met een tekst die rechtstreeks aan de gebruiker getoond mag worden. De
 * server formuleert zijn meldingen bewust generiek (geen "wachtwoord onjuist"),
 * dus nemen we ze letterlijk over; alleen als hij niets meegeeft vullen we zelf
 * iets in.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

/** Elke mapwijziging antwoordt met de verse lijst, zodat de UI niet hoeft na te vragen. */
export interface FolderMutation {
  folders: MailFolder[];
}

type UnauthorizedHandler = () => void;

let onUnauthorized: UnauthorizedHandler | null = null;

/**
 * De AuthContext registreert hier hoe een verlopen sessie opgeruimd wordt. Zo
 * hoeft geen enkele aanroepplek zelf op 401 te controleren.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

type AccountGoneHandler = (message: string) => void;

let onAccountGone: AccountGoneHandler | null = null;

/**
 * Eén mailbox is weg (afgemeld in een ander tabblad, of het wachtwoord is
 * elders gewijzigd) terwijl de andere nog aangemeld zijn. Dat is geen reden om
 * alles uit te loggen; de AuthContext haalt dan de actuele accountlijst op.
 */
export function setAccountGoneHandler(handler: AccountGoneHandler | null): void {
  onAccountGone = handler;
}

const ACCOUNT_GONE_CODES = new Set(["account_unknown", "account_signed_out"]);

/** Gedeelde afhandeling van een mislukt antwoord. */
async function failure(response: Response, quietUnauthorized: boolean): Promise<ApiError> {
  if (response.status === 401 && !quietUnauthorized) onUnauthorized?.();
  const error = await toApiError(response);
  if (response.status === 409 && ACCOUNT_GONE_CODES.has(error.code)) onAccountGone?.(error.message);
  return error;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  /**
   * Bij het opstarten is 401 het normale antwoord van /auth/me voor een bezoeker
   * die nog niet ingelogd is — dat mag geen "sessie verlopen" opleveren.
   */
  quietUnauthorized?: boolean;
  /** Welke mailbox; zonder opgave het actieve account. */
  account?: string | null;
}

function fallbackMessage(status: number): string {
  if (status === 401) return "Uw sessie is verlopen. Log opnieuw in.";
  if (status === 403) return "Deze actie is geweigerd. Ververs de pagina en probeer het opnieuw.";
  if (status === 404) return "Dit item bestaat niet meer.";
  if (status === 413) return "Het bestand is te groot.";
  if (status === 429) return "Te veel pogingen. Probeer het over een paar minuten opnieuw.";
  if (status >= 500) return "Er ging iets mis op de server. Probeer het later opnieuw.";
  return "Er ging iets mis. Probeer het opnieuw.";
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = `http_${response.status}`;
  let message = "";

  // Een foutantwoord hoeft geen JSON te zijn (nginx, proxy, rate-limiter).
  try {
    const body: unknown = await response.json();
    if (body !== null && typeof body === "object") {
      const record = body as Record<string, unknown>;
      if (typeof record["error"] === "string") code = record["error"];
      if (typeof record["message"] === "string") message = record["message"];
    }
  } catch {
    /* geen bruikbare body; de terugvaltekst hieronder volstaat */
  }

  return new ApiError(response.status, code, message || fallbackMessage(response.status));
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { accept: "application/json", ...baseHeaders(options.account) };
  if (method !== "GET") headers[CSRF_HEADER] = "1";
  if (options.body !== undefined) headers["content-type"] = "application/json";

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      // Het sessiecookie is httpOnly; zonder dit stuurt fetch het niet mee.
      credentials: "include",
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });
  } catch {
    // Netwerkfouten mogen nooit als rauwe TypeError doorlekken naar de UI.
    throw new ApiError(0, "network", "Geen verbinding met de server. Probeer het opnieuw.");
  }

  if (!response.ok) throw await failure(response, options.quietUnauthorized === true);

  if (response.status === 204) return undefined as T;
  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError(response.status, "bad_response", "Onverwacht antwoord van de server.");
  }
}

/** Mappaden bevatten punten en soms slashes; altijd door de encoder. */
function folderSegment(folder: string): string {
  return encodeURIComponent(folder);
}

/**
 * Pad naar één bijlage. De server serveert die als octet-stream met
 * Content-Disposition: attachment — nooit het Content-Type uit het bericht.
 */
export function attachmentUrl(folder: string, uid: number, partId: string, account?: string): string {
  return withAccount(
    `${BASE}/folders/${folderSegment(folder)}/messages/${uid}/attachments/${encodeURIComponent(partId)}`,
    account,
  );
}

/** Bron voor het sandboxed iframe met de gesaneerde HTML van een bericht. */
export function bodyUrl(folder: string, uid: number, showImages: boolean, account?: string): string {
  return withAccount(
    `${BASE}/folders/${folderSegment(folder)}/messages/${uid}/body?images=${showImages ? "1" : "0"}`,
    account,
  );
}

/**
 * De live-verbinding (Server-Sent Events) voor alle mailboxen van deze
 * browser. Een EventSource kan geen headers zetten; dat hoeft hier ook niet,
 * want de stroom gaat over de hele bundel.
 */
export const EVENTS_URL = `${BASE}/events`;

export const api = {
  /** Zonder account-header: een verdwenen account mag de lijst niet blokkeren. */
  me(quietUnauthorized = false): Promise<MeResponse> {
    return request<MeResponse>("/auth/me", { quietUnauthorized, account: null });
  },

  login(address: string, password: string, remember = false): Promise<MeResponse> {
    return request<MeResponse>("/auth/login", {
      method: "POST",
      body: { address, password, remember },
      // Een mislukte login is geen verlopen sessie; de loginpagina toont de
      // melding zelf.
      quietUnauthorized: true,
      account: null,
    });
  },

  logout(): Promise<void> {
    return request<void>("/auth/logout", { method: "POST", quietUnauthorized: true, account: null });
  },

  // ── Meerdere mailboxen ────────────────────────────────────────────────────

  /**
   * Nog een mailbox aanmelden in deze browser. Een fout wachtwoord geeft hier
   * net als bij inloggen een 401; dat is geen verlopen sessie, dus stil.
   */
  addAccount(address: string, password: string, remember: boolean): Promise<MeResponse> {
    return request<MeResponse>("/auth/accounts", {
      method: "POST",
      body: { address, password, remember },
      quietUnauthorized: true,
      account: null,
    });
  },

  /** Eén mailbox afmelden; geeft de overgebleven accounts, of niets als dit de laatste was. */
  removeAccount(id: string): Promise<MeResponse | undefined> {
    return request<MeResponse | undefined>(`/auth/accounts/${encodeURIComponent(id)}`, {
      method: "DELETE",
      account: null,
    });
  },

  /** Ongelezen post in het Postvak IN van elke aangemelde mailbox. */
  accountsUnread(): Promise<UnreadByAccount> {
    return request<UnreadByAccount>("/accounts/unread", { account: null });
  },

  folders(): Promise<MailFolder[]> {
    return request<MailFolder[]>("/folders");
  },

  // ── Mappenbeheer ──────────────────────────────────────────────────────────

  /*
   * Elke wijziging geeft de verse mappenlijst mee terug, dus na deze aanroepen
   * hoeft er geen losse api.folders() achteraan. Dat scheelt een verzoek en,
   * belangrijker, het voorkomt dat de poller er met een oud plaatje tussen komt.
   */

  /** `parent: null` = op het hoogste niveau; de server bouwt het pad. */
  createFolder(name: string, parent: string | null): Promise<FolderMutation & { path: string }> {
    return request("/folders", { method: "POST", body: { name, parent } });
  },

  /**
   * Hernoemen en verplaatsen zijn dezelfde handeling: IMAP RENAME neemt de hele
   * subboom mee. `parent: null` hangt de map op het hoogste niveau.
   */
  renameFolder(
    path: string,
    changes: { name?: string; parent?: string | null },
  ): Promise<FolderMutation & { path: string; previousPath: string }> {
    return request(`/folders/${folderSegment(path)}`, { method: "PATCH", body: changes });
  },

  /**
   * `recursive` is nodig zodra de map submappen heeft, `confirm` zodra er post
   * in zit. De server weigert zonder; dat is de bevestigingsstap.
   */
  deleteFolder(
    path: string,
    options: { recursive?: boolean; confirm?: boolean } = {},
  ): Promise<FolderMutation & { removed: string[] }> {
    const query = new URLSearchParams({
      recursive: options.recursive === true ? "1" : "0",
      confirm: options.confirm === true ? "1" : "0",
    });
    return request(`/folders/${folderSegment(path)}?${query.toString()}`, { method: "DELETE" });
  },

  messages(folder: string, offset: number, limit: number): Promise<MessageListPage> {
    const query = new URLSearchParams({ offset: String(offset), limit: String(limit) });
    return request<MessageListPage>(`/folders/${folderSegment(folder)}/messages?${query.toString()}`);
  },

  message(folder: string, uid: number): Promise<MessageDetail> {
    return request<MessageDetail>(`/folders/${folderSegment(folder)}/messages/${uid}`);
  },

  setFlags(
    folder: string,
    uid: number,
    changes: { seen?: boolean; flagged?: boolean },
  ): Promise<void> {
    return request<void>(`/folders/${folderSegment(folder)}/messages/${uid}/flags`, {
      method: "PATCH",
      body: changes,
    });
  },

  /**
   * Dezelfde vlaggen over een hele selectie. Bewust één verzoek: de server mag
   * per sessie maar twee IMAP-verbindingen tegelijk gebruiken, dus vijftig
   * losse aanroepen lopen gegarandeerd vast.
   */
  setFlagsBulk(
    folder: string,
    uids: number[],
    changes: { seen?: boolean; flagged?: boolean },
  ): Promise<void> {
    return request<void>(`/folders/${folderSegment(folder)}/flags`, {
      method: "POST",
      body: { uids, ...changes },
    });
  },

  // ── Ordenen ───────────────────────────────────────────────────────────────

  /**
   * Verwijderen gaat standaard naar de Prullenbak. Pas een tweede keer (of
   * verwijderen ván de Prullenbak) is definitief — de server bepaalt dat, niet
   * de client.
   */
  remove(folder: string, uids: number[], permanent = false): Promise<{ mode: string; count: number }> {
    return request(`/folders/${folderSegment(folder)}/delete`, {
      method: "POST",
      body: { uids, permanent },
    });
  },

  move(folder: string, uids: number[], target: string): Promise<{ moved: number; target: string }> {
    return request(`/folders/${folderSegment(folder)}/move`, {
      method: "POST",
      body: { uids, target },
    });
  },

  search(
    folder: string,
    q: string,
    scope: "all" | "from" | "subject" | "to",
    offset: number,
    limit: number,
  ): Promise<MessageListPage> {
    const query = new URLSearchParams({ q, scope, offset: String(offset), limit: String(limit) });
    return request<MessageListPage>(`/folders/${folderSegment(folder)}/search?${query.toString()}`);
  },

  // ── Opstellen ─────────────────────────────────────────────────────────────

  /**
   * Bijlage uploaden. Gaat als multipart naar de server, die hem streamend naar
   * schijf schrijft; pas bij verzenden wordt hij in de MIME opgenomen.
   */
  async uploadAttachment(file: File, account?: string): Promise<UploadedAttachment> {
    const form = new FormData();
    form.append("file", file, file.name);
    let response: Response;
    try {
      response = await fetch(`${BASE}/compose/attachments`, {
        method: "POST",
        credentials: "same-origin",
        // Bewust géén Content-Type zetten: de browser moet zelf de multipart
        // boundary bepalen.
        headers: { [CSRF_HEADER]: "1", ...baseHeaders(account) },
        body: form,
      });
    } catch {
      throw new ApiError(0, "network", "Geen verbinding met de server. Probeer het opnieuw.");
    }
    if (!response.ok) throw await failure(response, false);
    return (await response.json()) as UploadedAttachment;
  },

  discardAttachment(id: string, account?: string): Promise<void> {
    return request<void>(`/compose/attachments/${encodeURIComponent(id)}`, { method: "DELETE", account });
  },

  /** `account` is de afzender: zijn wachtwoord, zijn Verzonden-map, zijn handtekening. */
  send(draft: OutgoingMessage, account?: string): Promise<{ messageId: string; savedTo: string | null }> {
    return request("/compose/send", { method: "POST", body: draft, account });
  },

  saveDraft(draft: OutgoingMessage, account?: string): Promise<{ folder: string; uid: number | null }> {
    return request("/compose/draft", { method: "POST", body: draft, account });
  },

  // ── Meldingen ─────────────────────────────────────────────────────────────

  pushConfig(): Promise<{ enabled: boolean; publicKey: string | null }> {
    return request("/push/vapid");
  },

  pushSubscribe(subscription: PushSubscriptionJSON): Promise<void> {
    return request<void>("/push/subscribe", { method: "POST", body: subscription });
  },

  pushUnsubscribe(endpoint: string): Promise<void> {
    return request<void>("/push/unsubscribe", { method: "POST", body: { endpoint } });
  },

  pushStatus(): Promise<{ enabled: boolean; devices: number }> {
    return request("/push/status");
  },

  // ── Instellingen ──────────────────────────────────────────────────────────

  mailSettings(): Promise<MailSettings> {
    return request<MailSettings>("/profile/settings");
  },

  /**
   * Vraagt een eenmalig kaartje voor de profieldownload. In twee stappen omdat
   * een download een gewone navigatie is: die stuurt geen CSRF-header mee, en
   * een verlopen sessie zou anders als foutmelding in de map Downloads belanden.
   */
  profileTicket(withPassword: boolean): Promise<{ ticket: string; url: string; expiresInMs: number }> {
    return request("/profile/ticket", { method: "POST", body: { withPassword } });
  },

  /** Wijzigt het mailwachtwoord; geldt daarna overal (één wachtwoordbron). */
  changePassword(
    current: string,
    next: string,
    account?: string,
  ): Promise<{ changed: boolean; otherSessionsEnded: number }> {
    return request("/settings/password", { method: "POST", body: { current, next }, account });
  },

  signature(account?: string): Promise<SignatureSettings> {
    return request<SignatureSettings>("/settings/signature", { account });
  },

  /** Geeft de GESANEERDE versie terug, dus toon altijd wat hier uitkomt. */
  saveSignature(input: Partial<SignatureSettings>, account?: string): Promise<SignatureSettings> {
    return request<SignatureSettings>("/settings/signature", { method: "PUT", body: input, account });
  },

  /**
   * Logo voor in de handtekening. De server controleert het formaat aan de
   * eerste bytes en stuurt het bij verzenden als inline bijlage mee, zodat de
   * ontvanger het ziet zonder iets van buitenaf op te halen.
   */
  async uploadSignatureLogo(file: File, account?: string): Promise<SignatureSettings> {
    const form = new FormData();
    form.append("file", file, file.name);
    let response: Response;
    try {
      response = await fetch(`${BASE}/settings/signature/logo`, {
        method: "POST",
        credentials: "same-origin",
        // Geen Content-Type zetten: de browser bepaalt zelf de multipart boundary.
        headers: { [CSRF_HEADER]: "1", ...baseHeaders(account) },
        body: form,
      });
    } catch {
      throw new ApiError(0, "network", "Geen verbinding met de server. Probeer het opnieuw.");
    }
    if (!response.ok) throw await failure(response, false);
    return (await response.json()) as SignatureSettings;
  },

  removeSignatureLogo(account?: string): Promise<SignatureSettings> {
    return request<SignatureSettings>("/settings/signature/logo", { method: "DELETE", account });
  },
};

/**
 * Bron voor het logo van een account. `updatedAt` gaat als cache-buster mee,
 * want de server mag het plaatje vijf minuten cachen en een net geüpload logo
 * moet meteen zichtbaar zijn.
 */
export function signatureLogoUrl(updatedAt: number, account?: string): string {
  return withAccount(`${BASE}/settings/signature/logo?v=${updatedAt}`, account);
}
